import React, { Component } from 'react';
import _ from 'lodash';
import { IconButton } from '../common/IconButton';
import { Icon } from '../common/Icon';
import { ShortcutsManager } from '../common/ShortcutsManager';
import { prettyDuration } from '../common/TimeUtils';
import { OnOffIcon } from '../../../../components/common/OnOffIcon';
import NotesEditor from '../common/editors/NotesEditor';
import { Link } from 'react-router-dom';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { TimeFrom } from './TimeFrom';

const StatBadge = ({ stat, children, className, level, bg, ...other }) => {
  let [center, ... below] = children;
  return <div className={`StatBadge no-select  ${className || ''}`}>
    <span className={`text-center align-middle p-1 inline-block bg-${bg || 'secondary'}`} {...other}>
      <div className={`h5 m-0 monospace text-${level || 'white'}`}>{stat}</div>
      <div className={'small'}>{center}</div>
    </span>
    <div className={'small text-center text-secondary'}>{below}</div>
  </div>
}

// Someone else holds the task (legos/DataTask.js claim)
const isClaimedByOther = (task) => Boolean(task?.claim?.userId && task.claim.userId !== window.user.id);

export default class BatchTaskEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      paused: true,
      isAutoPause: false,
      draftNote: null
    };

    this.tasksService = window.client.service('services/data-tasks');
    this.claimService = window.client.service('services/data-tasks/claim');

    this.editorRef = React.createRef();

    // If no mouse/keyboard activity in 30 seconds, pause batch
    this.onActivityResetAutoPause = _.throttle(() => {
      clearTimeout(this.pauseTimeout);
      // Not on a task someone else holds: resuming would fail to claim it on every mouse move. Nor on a finished one:
      // working on it again takes a deliberate Resume, or it stays claimed after being done.
      const task = this.state.tasks?.[this.state.currentTask];
      if(this.state.paused && this.state.isAutoPause && !isClaimedByOther(task) && !task?.finishedAt) {
        this.start().catch(err => console.error('Error resuming task', err));
      }
      this.pauseTimeout = setTimeout(() => {
        if(!this.state.paused) {
          console.log(new Date().toLocaleTimeString()+' AUTOPAUSE -----')
          this.pause(true);
        }
      }, this.state.tasks?.[0]?.typeId === 'fuseboxGenAssignment' ? 60000*2 : 30000)
    }, 1000);

    this.onOtherTabActivity = this.onOtherTabActivity.bind(this);
    this.broadCastTaskInProgress = this.broadCastTaskInProgress.bind(this);
    this.onTaskChanged = this.onTaskChanged.bind(this);
  }

  componentDidMount() {
    this.loadBatch().catch((err => alert(err)));

    // Edits and claims by others
    this.tasksService.on('updated', this.onTaskChanged);

    $('html').mousemove(this.onActivityResetAutoPause);
    $('html').keydown(this.onActivityResetAutoPause);
    // Make sure key events captured by shortcuts also renew autoPause time
    ShortcutsManager.onAnyShortcut(this.onActivityResetAutoPause)
    this.onActivityResetAutoPause();

    this.context.page.shortcuts.registerKeyMappings({
      'prevTask': ['ctrl+alt+left'],
      'nextTask': ['ctrl+alt+right'],
    });

    this.context.page.shortcuts.registerActionHandlers({
      'prevTask': () => this.previous(),
      'nextTask': () => this.next(),
    });
  }

  onOtherTabActivity(e) {
    if(e.data?.event === 'activity') {
      console.log(new Date().toLocaleTimeString()+' ACTIVIDAD, HOJALDRE')
      this.onActivityResetAutoPause()
    } else if(e.data?.event === 'taskStarted') {
      // Another window started working on a task: one timer at a time, or the worked time counts twice
      this.pause(false).catch(err => console.error('Error pausing for another window', err));
    }
  }

  componentWillUnmount() {
    // Remove handler so that once component is destroyed, it does not continue as ghost
    this.unmounted = true;
    clearTimeout(this.pauseTimeout);
    this.tasksService.removeListener('updated', this.onTaskChanged);
    $('html').unbind('mousemove', this.onActivityResetAutoPause);
    $('html').unbind('keydown', this.onActivityResetAutoPause);
    this.context.page.tabsChannel.removeEventListener('message', this.onOtherTabActivity)
    ShortcutsManager.offAnyShortcut(this.onActivityResetAutoPause)

    if(!this.state.paused) {
      this.pause(false);
    }
  }

  async loadBatch() {
    const tasks = await this.props.loadBatchTasks();

    const firstNonFinishedTask = _.findIndex(tasks, t => !t.finishedAt);
    await this.openTask(!_.isNumber(this.props.initialTask) ? Math.max(0, firstNonFinishedTask) :this.props.initialTask );
    this.setState({ loading: false, tasks })
  }

  async saveWorkIntervalSoFar() {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    // Stop working on task, update task total time and save worked interval. Not on a task someone else holds: the
    // write would be rejected.
    if (currentTask && !this.state.paused && !isClaimedByOther(currentTask)) {
      const { workIntervalStart } = this.state;
      const duration = new Date() - workIntervalStart;
      await this.updateTask(this.state.currentTask, {
        $push: { workedIntervals: { start: workIntervalStart, duration, userId: window.user.id } },
        $inc: { workedTotalTime: duration },
        $min: { startedAt: workIntervalStart },
      });
    }
  }

  // Mongo updates of only what changed, so they never clobber what others wrote
  async updateTask(taskIndex, update) {
    const task = this.state.tasks[taskIndex];
    update = { ...update, $set: { ...update.$set, updatedBy: this.props.user } };
    this.onTaskChanged(await this.tasksService.update(task._id, update));
  }

  onTaskChanged(changedTask) {
    const { tasks, currentTask, paused } = this.state;
    const index = _.findIndex(tasks, t => t._id === changedTask._id);
    if (index === -1) {
      return;
    }
    tasks[index] = changedTask;
    this.setState({ tasks });

    // Someone else took the task we work on: stop
    if (index === currentTask && !paused && isClaimedByOther(changedTask)) {
      this.pause(false).catch(err => console.error('Error pausing a task taken over', err));
    }
  }

  // False when someone else holds the task. Takes an index: openTask claims right after changing currentTask.
  async claimTask(taskIndex) {
    const task = this.state.tasks[taskIndex];
    const claimedTask = await this.claimService.create({ taskId: task._id, action: 'claim' });
    this.onTaskChanged(claimedTask);
    return !isClaimedByOther(claimedTask);
  }

  // A finished task is held only while worked on: there's no "done" left to release it
  async releaseIfFinished(taskIndex) {
    const task = this.state.tasks?.[taskIndex];
    if (task?.finishedAt && task.claim?.userId === window.user.id) {
      this.onTaskChanged(await this.claimService.create({ taskId: task._id, action: 'release' }));
    }
  }

  // Gives back a task we won't finish (finishing it, or marking it for revision, releases it by itself)
  async releaseTask() {
    if (!this.state.paused) {
      await this.pause(false);
    }
    const task = this.state.tasks[this.state.currentTask];
    this.onTaskChanged(await this.claimService.create({ taskId: task._id, action: 'release' }));
  }

  async takeOver() {
    const { _id, claim } = this.state.tasks[this.state.currentTask];
    if (!claim) {
      return;
    }
    const note = window.prompt(`${claim.username} holds this task since ${new Date(claim.claimedAt).toLocaleString()}. Take it over?\n\nNote left on the task:`, `Took over from ${claim.username}`);
    if (note !== null) {
      this.onTaskChanged(await this.claimService.create({ taskId: _id, action: 'takeOver', note }));
    }
  }

  // Claim (without starting), release, or take over the current task
  getClaimControl(task) {
    if (isClaimedByOther(task)) {
      return <IconButton icon={'pan_tool'} level={'warning'} title={`${task.claim.username} holds this task`} onClick={() => this.takeOver().catch(alert)}>
        Take over from {task.claim.username}
      </IconButton>;
    } else if (task.claim?.userId) {
      return <IconButton icon={'lock_open'} level={'secondary'} title={'Release the task, for someone else to do it'} onClick={() => this.releaseTask().catch(alert)}>
        Release
      </IconButton>;
    } else {
      return <IconButton icon={'lock'} level={'success'} title={'Claim the task, without starting it'} onClick={() => this.claimTask(this.state.currentTask).catch(alert)}>
        Claim
      </IconButton>;
    }
  }

  async pause(isAutoPause = false) {
    clearInterval(this.broadcastingInterval);
    $(window).unbind('beforeunload', this.preventClose);

    try {
      this.context.page.tabsChannel.postMessage({ event: 'taskInProgressPaused' })
    }catch(e) {
      console.error('Error sending message to other tabs', e.toString())
    }

    if(!isAutoPause) {
      this.context.page.tabsChannel.removeEventListener('message', this.onOtherTabActivity)
    }

    try {
      await this.saveWorkIntervalSoFar();
    } finally {
      // Even when the save fails
      this.setState({ paused: true, isAutoPause });
      await this.releaseIfFinished(this.state.currentTask);
    }
  }

  preventClose(e) {
    return 'Vas a perder el tiempo de tu task!!!'; //This is displayed on the dialog
  }

  broadCastTaskInProgress() {
    const task = this.state.tasks[this.state.currentTask];
    const { batchId, workedTotalTime } = task;
    const workIntervalStart = this.state.workIntervalStart;
    this.context.page.tabsChannel.postMessage({event: 'taskInProgress', batchId, workIntervalStart, workedTotalTime})
  }

  async start() {
    const taskIndex = this.state.currentTask;
    if (!await this.claimTask(taskIndex)) {
      // Someone else holds it: show who, even if its overlay was closed
      this.setState({ closedClaimOverlay: null });
      return;
    }
    // The user moved to another task, or left the page, while claiming
    if (taskIndex !== this.state.currentTask || this.unmounted) {
      await this.releaseIfFinished(taskIndex);
      return;
    }

    this.context.page.tabsChannel.postMessage({ event: 'taskStarted' });
    this.context.page.tabsChannel.addEventListener('message', this.onOtherTabActivity);
    $(window).on('beforeunload', this.preventClose);

    clearInterval(this.broadcastingInterval);
    this.broadcastingInterval = setInterval(this.broadCastTaskInProgress, 1000);
    this.broadCastTaskInProgress();
    this.setState({ paused: false, workIntervalStart: new Date() })
  }

  async openTask(taskIndexInBatch) {
    // Landing on a finished task stops the timer: working on it again takes a deliberate Resume
    if (!this.state.paused && this.state.tasks?.[taskIndexInBatch]?.finishedAt) {
      await this.pause(false);
    }
    await this.saveWorkIntervalSoFar();
    if (taskIndexInBatch !== this.state.currentTask) {
      await this.releaseIfFinished(this.state.currentTask);
    }
    this.setState({ workIntervalStart: new Date(), currentTask: taskIndexInBatch })

    if(this.props.onCurrentTaskChange) {
      this.props.onCurrentTaskChange(taskIndexInBatch);
    }

    await this.claimIfWorking(taskIndexInBatch);
  }

  // While working, the task we land on must be ours: claim it, or stop when someone else holds it
  async claimIfWorking(taskIndex) {
    if (this.state.paused) {
      return;
    }
    let claimed = false;
    try {
      claimed = await this.claimTask(taskIndex);
    } finally {
      // Also when the claim request fails
      if (!claimed) {
        await this.pause(false);
      }
    }
  }

  // The arrows reach any task, also one someone else holds; done-and-next skips those, as we can't work on them
  isSkipped(task, skipHeldByOthers) {
    return (skipHeldByOthers && isClaimedByOther(task)) || (this.state.hideComplete && task.state === 'complete');
  }

  async next(skipHeldByOthers = false) {
    const current = this.state.currentTask;

    const nextTaskIndex = _.findIndex(this.state.tasks, (t,i) => i > current && !this.isSkipped(t, skipHeldByOthers));

    if (nextTaskIndex >= 0) {
      await this.openTask(nextTaskIndex)
    } else {
      await this.pause(false);
    }
  }

  async previous() {
    const current = this.state.currentTask;

    const prevTaskIndex = _.findLastIndex(this.state.tasks, (t,i) => i < current && !this.isSkipped(t));

    if (prevTaskIndex >= 0) {
      await this.openTask(prevTaskIndex)
    }
  }


  async doneAndNext(output = undefined) {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      const $set = { state: 'complete', finishedAt: new Date() };

      if(output !== undefined) {
        $set.output = output;
      }

      await this.updateTask(this.state.currentTask, { $set });
      if(!this.state.paused) {
        await this.next(true);
      }
    }
  }

  async markForRevision() {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      // The revision releases the task: stop working first, so the worked time is saved while it's ours
      if (!this.state.paused) {
        await this.pause(false);
      }
      await this.updateTask(this.state.currentTask, { $set: { state: 'needsRevision' } });
    }
  }

  async markRevisionDone() {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      await this.updateTask(this.state.currentTask, { $set: { state: currentTask.finishedAt ? 'complete' : '' } });
      // Becoming complete releases the claim: we're still working on it
      await this.claimIfWorking(this.state.currentTask);
    }
  }

  async reopenTask() {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      const $set = { finishedAt: null };
      if(currentTask.state !== 'needsRevision') {
        $set.state = '';
      }
      await this.updateTask(this.state.currentTask, { $set });
    }
  }

  async updateNotes(updatedNotes) {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      await this.updateTask(this.state.currentTask, { $set: { notes: updatedNotes } });
      this.setState({ draftNote: null })
    }
  }

  getTaskStateControls() {
    let currentTask = (this.state.tasks || {})[this.state.currentTask];
    if (currentTask) {
      const {state, finishedAt, notes} = currentTask;


      let btnMarkAsDone = null;
      if(finishedAt) {
        btnMarkAsDone = <IconButton icon={'undo'} level={'primary'} onClick={() => this.reopenTask()}>
          Reopen task
        </IconButton>;
      } else {
        btnMarkAsDone = <IconButton icon={'done'} level={'success'} onClick={() => this.doneAndNext()}>
          Mark as done
        </IconButton>;
      }

      let btnMarkForRevision;
      if(state === 'needsRevision') {
        btnMarkForRevision = <IconButton icon={'assignment_turned_in'} level={'primary'}
                                         onClick={() => this.markRevisionDone()}>

          Close revision
        </IconButton>;
      } else {
        btnMarkForRevision = <IconButton icon={'live_help'} level={'warning'}
                                         onClick={() => this.markForRevision()}>

          Mark for revision
        </IconButton>;
      }

      return <React.Fragment>
        <div className={'small text-center text-secondary mt-2'}>Task {currentTask._id}</div>

        <div className={'text-center d-flex align-items-center justify-content-around mt-2'}>
          {btnMarkForRevision} {btnMarkAsDone}
        </div>

        <div className={'border border-left-0 border-right-0 border-bottom-0 mb-4'}>
          <NotesEditor value={notes} onChange={updatedNotes => this.updateNotes(updatedNotes)}/>
        </div>
      </React.Fragment>
    } else {
      return null;
    }
  }

  getTaskStateColor(task, taskIndex, currentTask) {
    let stateColor = 'secondary';
    if (taskIndex === currentTask) {
      if (this.state.paused) {
        stateColor = 'light-primary';
      } else {
        stateColor = 'primary';
      }
    } else if (task.finishedAt) {
      stateColor = 'light-success';
    } else if (task.workedTotalTime > 0) {
      stateColor = 'light-warn';
    }
    return stateColor;
  }

  getTaskIcon(task, taskIndex, currentTask) {

    if (isClaimedByOther(task)) {
      return <Icon level={'warning'} icon={'lock'} title={`${task.claim.username} holds it`}/>;
    } else if (task.state === 'needsRevision') {
      return <Icon level={'warning'} icon={'help'}/>;
    } else if (task.finishedAt) {
      return <Icon level={'success'} icon={'done'}/>;
    } else if (taskIndex === currentTask) {
      if (this.state.paused) {
        return <Icon level={'primary'} icon={'pause'}/>;
      } else {
        return <Icon level={'white'} className={'animate-rotating-slow position-relative'} icon={'refresh'}/>;
      }
    } else if (task.startedAt) {
      return <Icon level={'warning'} icon={'pause'}/>;
    } else {
      return <Icon level={'dark'} icon={'more_horiz'}/>
    }
  }

  render() {
    const { getTaskComponent } = this.props;
    const { loading, workIntervalStart, tasks, currentTask, paused, hideComplete } = this.state;

    if (loading) {
      return <div ref={this.editorRef} className={'p-3'}>Loading tasks...</div>
    } else if(!tasks?.length) {
      return <div className={'p-2 text-center'}>Batch appears to have no tasks</div>
    } else {
      const task = tasks[currentTask];

      const { batchId, typeId, workedTotalTime } = task;

      const taskComponent = getTaskComponent(task, tasks, this);

      let batchAverage = null;
      let totalBatchTime = 0;
      const numberOfStartedTasks = _.filter(tasks, t => t.workedTotalTime > 0).length;
      if (numberOfStartedTasks > 0) {
        totalBatchTime = _.sumBy(tasks, t => t.workedTotalTime || 0);
        batchAverage = totalBatchTime / numberOfStartedTasks;
      }

      let activeTaskPairs = _.map(tasks, (t,i) => [t,i]);
      if(hideComplete)
        activeTaskPairs = _.filter(activeTaskPairs, ([t,i]) => t.state !== 'complete' || t === task);

      const shownTasks = 9;
      let sliceFrom = Math.max(0, Math.floor(_.findIndex(activeTaskPairs, p => p[0] == task) - (shownTasks -1) / 2));
      let sliceTo = sliceFrom + shownTasks;
      // Reaching the end, ensure all tasks are shown
      if (sliceTo > activeTaskPairs.length) {
        sliceFrom = Math.max(0, activeTaskPairs.length - shownTasks);
        sliceTo = activeTaskPairs.length + 1;
        console.log("slice", sliceFrom, sliceTo)
      }


      const tasksSlice = activeTaskPairs.slice(sliceFrom, sliceTo);

      return <div ref={this.editorRef} className={'BatchTaskEditor'}>
        {/* The counter sits at the center with a fixed width, so the arrows next to it never move */}
        <div className={'bg-dark p-1 px-2 text-white align-items-center zoom-75'} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)' }}>

          <div className={'d-flex align-items-center justify-content-between'}>
            <div className={'align-middle inline-block ml-1 mr-3 h6'}>
              <div><Link to={`/tasks/batches?${this.context.page.getUrlParamComponent('filters')}`}><Icon icon={'arrow_back'}/> {typeId}</Link></div>
              <div><span className={'text-light ml-4'}>{batchId}</span></div>
            </div>

            <div className={'m-0 no-select text-nowrap'}>
              <div className={'pauseStartButton inline-block'}>

                {
                  paused ?
                    <IconButton icon={'play_circle_filled'} size={'xl'} level={isClaimedByOther(task) ? 'secondary' : 'success'} className={'text-decoration-none'}
                                disabled={isClaimedByOther(task)} title={isClaimedByOther(task) ? `${task.claim.username} holds this task` : undefined}
                                onClick={() => this.start().catch(alert)}>
                      <span className={'h4 align-middle'}>Resume task</span>
                    </IconButton>
                    :
                    <IconButton icon={'pause_circle_filled'} size={'xl'} className={'text-decoration-none'} onClick={() => this.pause().catch(alert)}>
                      <span className={'h4 align-middle'}>Pause task</span>
                    </IconButton>
                }
              </div>

              <IconButton icon={'first_page'} level={'primary'} onClick={() => this.openTask(0)}/>

              <IconButton icon={'arrow_back_ios'} size={'lg'} onClick={() => this.previous()}/>
            </div>
          </div>

          <span className={'h4 m-0 text-center no-select'} style={{ width: `${2 * String(tasks.length).length + 2.4}ch`, fontVariantNumeric: 'tabular-nums' }}>
            {currentTask + 1}
            <span className={'small text-secondary m-2'}>of</span>
            {tasks.length}
          </span>

          <div className={'d-flex align-items-center justify-content-between'}>
            <div className={'no-select text-nowrap'}>
              <IconButton icon={'arrow_forward_ios'}  size={'lg'} onClick={() => this.next()}/>

              <IconButton icon={'last_page'} level={'primary'} onClick={() => this.openTask(tasks.length - 1)}/>

              <span className={'ml-3'}>{this.getClaimControl(task)}</span>
            </div>

            <div>
              <span>
                <StatBadge stat={prettyDuration(totalBatchTime)} bg={`light-primary`}>
                  <Icon icon={'functions'} level={'primary'}/>
                  Total
                </StatBadge>

                <StatBadge stat={prettyDuration(batchAverage)} bg={`light-info`} className={'mr-4'}>
                  <Icon icon={'timer'} level={'info'}/>
                  Avg
                </StatBadge>
              </span>

              <span>

                 {
                   sliceFrom > 0 ?
                     <StatBadge stat={ '...'} bg={'light-secondary'} key={'smaller'} onClick={() => this.openTask(activeTaskPairs[Math.max(0, sliceFrom - (shownTasks+1)/2)][1])}>
                       <div>{sliceFrom} more</div>
                       <span>&nbsp;</span>
                     </StatBadge>
                     :
                     <StatBadge stat={''} bg={'secondary'} key={'start'}>
                       <div></div>
                       <span>&nbsp;</span>
                     </StatBadge>
                 }

                {
                  _.map(tasksSlice, ([t,i], j) => {
                    const taskNumber = i;
                    const taskIcon = this.getTaskIcon(t, taskNumber, currentTask)
                    const taskColor = this.getTaskStateColor(t, taskNumber, currentTask);

                    let time = t.workedTotalTime ? prettyDuration(t.workedTotalTime) :
                      <span className={'translucent'}>-</span>;
                    if (taskNumber === currentTask && !paused) {
                      time = <TimeFrom from={this.state.workIntervalStart} extra={workedTotalTime}/>;
                    }

                    return <StatBadge className={j && tasksSlice[j-1][1] !== (i-1) ? 'ml-2' : ''}
                                      stat={time} bg={taskColor} key={taskNumber}
                                      onClick={() => this.openTask(taskNumber)}>
                      <div>{taskIcon}</div>
                      {taskNumber + 1}
                    </StatBadge>
                  })
                }

                {
                  sliceTo < activeTaskPairs.length ?
                    <StatBadge stat={"..."} bg={'light-secondary'} key={'more'}  onClick={() => this.openTask(activeTaskPairs[Math.min(activeTaskPairs.length - 1, sliceTo + (shownTasks-1)/2)][1])}>
                      <div>{activeTaskPairs.length - sliceTo} more</div>
                      <span>&nbsp;</span>
                    </StatBadge>
                    :
                    <StatBadge stat={'Finish'} bg={'success'} key={'finish'}>
                      <div>line</div>
                      <span>&nbsp;</span>
                    </StatBadge>
                }

              </span>

              <span className={'ml-2 pb-3 inline-block align-middle'}>
                <OnOffIcon value={hideComplete} onChange={(v) => this.setState({hideComplete: v})} icon={'remove_done'} title={'Hide complete tasks'}/>
              </span>

              <span className={'inline-block text-secondary text-center align-middle ml-1'}>

              </span>
            </div>
          </div>
        </div>

        <div className={`BatchTaskEditor__workArea bg-dark task-${paused ? 'paused' : 'started'}`}>
          {taskComponent}
          {
            // Closable, to look at the task
            isClaimedByOther(task) && this.state.closedClaimOverlay !== task._id &&
            <div className={'BatchTaskEditor__claimed'}>
              <div className={'bg-dark text-white p-4 rounded text-center position-relative'}>
                <span className={'position-absolute'} style={{ top: 4, right: 4 }}>
                  <IconButton icon={'close'} level={'secondary'} title={'Close, to look at the task'} onClick={() => this.setState({ closedClaimOverlay: task._id })}/>
                </span>
                <div className={'h4'}>
                  <Icon icon={'lock'} level={'warning'}/> {task.claim.username} holds this task
                </div>
                <div className={'text-secondary mb-3'}>since {new Date(task.claim.claimedAt).toLocaleString()}</div>
                <IconButton icon={'pan_tool'} level={'warning'} onClick={() => this.takeOver().catch(alert)}>
                  Take over
                </IconButton>
              </div>
            </div>
          }
        </div>
      </div>
    }
  }
}

BatchTaskEditor.contextType = LegoAdminPageContext;
